import {
  EditGuarantees,
  GuaranteeTypeEnum,
  RequestGuarantees
} from '@insurance/api/requests'
import {
  DEFAULT_DATE_FORMAT,
  deviceSession,
  formatDateToISOFormat
} from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

import { GuaranteesFormValues } from './type'
import {
  getBeneficiaryFields,
  getContractorFields,
  getCustomerFields
} from '../PersonsFields'

export const GUARANTEES_FORM_DATA_KEY = '__guarantees_insurance_form_data__'

export const STEP = {
  GUARANTEE: 0,
  PARTIES: 1,
  MEDIA: 2,
  CUSTOMER: 3
} as const

/** Types whose beneficiary the backend fills in and the manager cannot edit. */
const FIXED_BENEFICIARY_TYPES = new Set<GuaranteeTypeEnum>([
  GuaranteeTypeEnum.CUSTOMS,
  GuaranteeTypeEnum.ATA_CARNET
])

export const hasFixedBeneficiary = (type?: GuaranteeTypeEnum) =>
  !!type && FIXED_BENEFICIARY_TYPES.has(type)

export const GUARANTEE_TYPE_LABEL_KEY: Record<GuaranteeTypeEnum, string> = {
  [GuaranteeTypeEnum.OFFER]: 'guarantee_type_offer',
  [GuaranteeTypeEnum.PERFORMANCE]: 'guarantee_type_performance',
  [GuaranteeTypeEnum.CUSTOMS]: 'guarantee_type_customs',
  [GuaranteeTypeEnum.ATA_CARNET]: 'guarantee_type_ata_carnet'
}

export const FIXED_BENEFICIARY_LABEL_KEY: Partial<
  Record<GuaranteeTypeEnum, string>
> = {
  [GuaranteeTypeEnum.CUSTOMS]: 'customs_service_md',
  [GuaranteeTypeEnum.ATA_CARNET]: 'chamber_of_commerce_md'
}

export const saveGuaranteesValuesToStorage = (
  values: GuaranteesFormValues,
  id: string | number = ''
) => {
  sessionStorage.setItem(id + GUARANTEES_FORM_DATA_KEY, JSON.stringify(values))
}

export const getGuaranteesValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + GUARANTEES_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values) as GuaranteesFormValues

  return {
    ...parsedValues,
    ...(parsedValues.valid_from && {
      valid_from: dayjs(parsedValues.valid_from)
    }),
    ...(parsedValues.valid_to && {
      valid_to: dayjs(parsedValues.valid_to)
    })
  }
}

export const removeGuaranteesValuesFromStorage = (id: string | number = '') => {
  sessionStorage.removeItem(GUARANTEES_FORM_DATA_KEY)
  sessionStorage.removeItem(id + GUARANTEES_FORM_DATA_KEY)
}

/** Root of the type-specific block; `{variables}` name its fields. */
export const DETAILS_ROOT_NAME = 'details'

const VARIABLE_PATTERN = /\{([a-zA-Z0-9_]+)\}/g

const formatVariableValue = (value: unknown) => {
  if (dayjs.isDayjs(value)) return value.format(DEFAULT_DATE_FORMAT)

  if (typeof value === 'number') return String(value)

  if (typeof value === 'string' && value.trim()) return value

  return ''
}

/**
 * Replaces the `{placeholders}` of an insured-risks blank with what the manager
 * has already filled in. A variable with no value yet is left as it is — the
 * backend substitutes whatever is still a placeholder when the policy is
 * printed.
 */
export const fillInsuredRisksVariables = (
  text: string,
  getFieldValue: (name: NamePath) => unknown
) =>
  text.replace(VARIABLE_PATTERN, (placeholder, name: string) => {
    const value =
      getFieldValue([DETAILS_ROOT_NAME, name]) ?? getFieldValue(name)

    return formatVariableValue(value) ?? placeholder
  })

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ?? (() => '')

  const detailsRootName = DETAILS_ROOT_NAME

  return {
    guaranteeTypeProps: { name: 'guarantee_type', label: t('guarantee_type') },
    validFromProps: { name: 'valid_from', label: t('valid_from') },
    validToProps: { name: 'valid_to', label: t('valid_to') },
    guaranteedObligationProps: {
      name: 'guaranteed_obligation',
      label: t('insured_risks')
    },
    currencyProps: { name: 'currency', label: t('currency') },
    guaranteedAmountProps: {
      name: 'amount',
      label: t('guaranteed_amount')
    },
    premiumProps: { name: 'premium', label: t('insurance_premium') },
    installmentsProps: { name: 'installments', label: t('installments') },

    // details.* — one object in the form, subset picked per type on submit
    detailsProps: { name: detailsRootName },
    tenderProps: { name: [detailsRootName, 'tender'], label: t('nr_tender') },
    tenderObjectProps: {
      name: [detailsRootName, 'tender_object'],
      label: t('tender_object')
    },
    guaranteedContractProps: {
      name: [detailsRootName, 'guaranteed_contract'],
      label: t('guaranteed_contract')
    },
    contractObjectProps: {
      name: [detailsRootName, 'contract_object'],
      label: t('contract_object')
    },
    customsRegimeProps: {
      name: [detailsRootName, 'customs_regime'],
      label: t('customs_regime')
    },
    customsReferenceProps: {
      name: [detailsRootName, 'customs_reference'],
      label: t('customs_reference')
    },
    commercialContractProps: {
      name: [detailsRootName, 'commercial_contract'],
      label: t('commercial_contract')
    },
    advanceAmountProps: {
      name: [detailsRootName, 'advance_amount'],
      label: t('advance_amount')
    },
    carnetNumberProps: {
      name: [detailsRootName, 'carnet_number'],
      label: t('carnet_number')
    },
    requestedAmountProps: {
      name: [detailsRootName, 'requested_amount'],
      label: t('requested_amount')
    },
    goodsProps: {
      name: [detailsRootName, 'goods'],
      label: t('ata_carnet_goods')
    },

    contractorProps: { name: 'contractor', label: t('contractor') },
    beneficiaryProps: { name: 'beneficiary', label: t('beneficiary') },
    contractorCompanyNameProps: {
      name: ['contractor', 'legal_entity', 'name'],
      label: t('contractor')
    },
    beneficiaryCompanyNameProps: {
      name: ['beneficiary', 'legal_entity', 'name'],
      label: t('beneficiary')
    },
    customerProps: { name: 'customer', label: t('customer') },

    // Hidden
    stepKey: { name: 'step' },
    priceKey: { name: 'price' },
    orderKey: { name: 'order_id' }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

const normalizeDetails = (values: GuaranteesFormValues) => {
  const details = values.details

  if (!details) return {}

  switch (values.guarantee_type) {
    case GuaranteeTypeEnum.OFFER:
      return {
        tender: details.tender,
        tender_object: details.tender_object
      }

    case GuaranteeTypeEnum.PERFORMANCE:
      return {
        guaranteed_contract: details.guaranteed_contract,
        contract_object: details.contract_object
      }

    case GuaranteeTypeEnum.CUSTOMS:
      return {
        customs_regime: details.customs_regime,
        customs_reference: details.customs_reference
      }

    case GuaranteeTypeEnum.ATA_CARNET:
      return {
        goods: (details.goods || []).map((item) => ({
          description: item.description,
          quantity: String(item.quantity),
          value: String(item.value),
          notes: item.notes || undefined
        }))
      }

    default:
      return {}
  }
}

export const normalizeGuaranteesData = (
  values: GuaranteesFormValues,
  useSession = true
): RequestGuarantees => {
  const session = deviceSession()

  return {
    guarantee_type: values.guarantee_type,
    valid_from: formatDateToISOFormat(values.valid_from)!,
    valid_to: formatDateToISOFormat(values.valid_to)!,
    currency: values.currency,
    amount: String(values.amount),
    premium: String(values.premium),
    installments: values.installments,
    guaranteed_obligation: values.guaranteed_obligation,
    contractor: getContractorFields(values),
    beneficiary: hasFixedBeneficiary(values.guarantee_type)
      ? undefined
      : getBeneficiaryFields(values),
    details: normalizeDetails(values),
    ...(useSession && {
      session: {
        device_id: session.deviceId,
        session_id: session.sessionId
      }
    })
  } as RequestGuarantees
}

/**
 * PATCH payload. Always the complete block — EditGuarantees documents that a
 * PATCH replaces `Order.data` rather than merging into it, so anything omitted
 * here is silently dropped from the stored case.
 */
export const normalizeEditData = (
  values: GuaranteesFormValues
): EditGuarantees => {
  const base = normalizeGuaranteesData(values, false)

  return {
    ...base,
    customer: values.customer ? getCustomerFields(values.customer) : undefined
  } as EditGuarantees
}
